import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Mail, MailOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorText, formatPrice } from "@/lib/api";

function Inbox({ messages, reload }) {
  const toggle = async (m) => {
    await api.patch(`/admin/messages/${m.id}`, { read: !m.read });
    reload();
  };
  const remove = async (m) => {
    await api.delete(`/admin/messages/${m.id}`);
    reload();
  };
  if (!messages.length) return <p data-testid="inbox-empty" className="py-16 text-center font-mono text-sm text-zinc-400">No messages yet.</p>;
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <div key={m.id} data-testid={`inbox-message-${m.id}`} className={`border p-5 ${m.read ? "border-white/10 bg-surface" : "border-[rgba(212,230,10,0.45)] bg-[#1a1b12]"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest">
                <span className={m.kind === "quote" ? "text-rescue" : "text-volt"}>{m.kind === "quote" ? "Quote request" : "Contact"}</span>
                <span className="text-zinc-400"> / {new Date(m.created_at).toLocaleString()} / email {m.email_status}</span>
              </p>
              <p className="mt-1 font-display text-2xl font-bold">{m.name}</p>
              <p className="text-sm text-zinc-300">
                <a href={`mailto:${m.email}`} className="hover:text-volt">{m.email}</a>
                {m.phone && ` / ${m.phone}`}
                {m.organization && ` / ${m.organization}`}
              </p>
              {m.product && <p className="mt-1 font-mono text-xs text-volt">{m.product}</p>}
            </div>
            <div className="flex gap-2">
              <button data-testid={`inbox-toggle-read-${m.id}`} onClick={() => toggle(m)} className="grid h-9 w-9 place-items-center border border-white/15 hover:border-[var(--volt)]" title="Toggle read">
                {m.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
              </button>
              <button data-testid={`inbox-delete-${m.id}`} onClick={() => remove(m)} className="grid h-9 w-9 place-items-center border border-white/15 hover:border-[var(--rescue)]" title="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-4 whitespace-pre-wrap text-zinc-200">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function PriceRow({ p, reload }) {
  const [val, setVal] = useState(p.price ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (clear) => {
    setBusy(true);
    try {
      await api.patch(`/admin/products/${p.slug}`, clear ? { clear_price: true } : { price: Number(val) });
      toast.success(clear ? "Set to quote-only" : "Price saved");
      if (clear) setVal("");
      reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`admin-product-${p.slug}`} className="grid items-center gap-3 border-b border-white/10 py-4 md:grid-cols-[1fr_auto]">
      <div>
        <p className="font-display text-xl font-bold uppercase">{p.name}</p>
        <p className="font-mono text-xs text-zinc-400">{p.model} / {p.price ? formatPrice(p.price) : "Quote only"}</p>
      </div>
      <div className="flex gap-2">
        <input data-testid={`admin-price-input-${p.slug}`} type="number" min="0" step="0.01" placeholder="Price USD" value={val} onChange={(e) => setVal(e.target.value)} className="h-10 w-32 border border-white/15 bg-transparent px-3 text-white focus:border-[var(--volt)] focus:outline-none" />
        <button data-testid={`admin-price-save-${p.slug}`} disabled={busy || val === ""} onClick={() => save(false)} className="h-10 bg-volt px-4 font-mono text-xs font-bold uppercase text-black disabled:opacity-40">Save</button>
        <button data-testid={`admin-price-clear-${p.slug}`} disabled={busy || !p.price} onClick={() => save(true)} className="h-10 border border-white/15 px-3 font-mono text-xs uppercase disabled:opacity-40">Quote only</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [ok, setOk] = useState(false);
  const [messages, setMessages] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = async () => {
    try {
      const [m, p, o] = await Promise.all([api.get("/admin/messages"), api.get("/products"), api.get("/admin/orders")]);
      setMessages(Array.isArray(m.data) ? m.data : []);
      setProducts(Array.isArray(p.data) ? p.data : []);
      setOrders(Array.isArray(o.data) ? o.data : []);
      qc.invalidateQueries({ queryKey: ["products"] });
      setOk(true);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("bamf_admin_token");
    nav("/admin/login");
  };

  if (!ok) return <div className="min-h-screen bg-obsidian" />;
  const unread = messages.filter((m) => !m.read).length;
  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-obsidian px-5 pb-24 pt-28 md:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-volt">Command Center</p>
            <h1 className="font-display text-5xl md:text-6xl font-black uppercase">Admin</h1>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="flex h-10 items-center gap-2 border border-white/15 px-4 font-mono text-xs uppercase hover:border-[var(--rescue)]">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
        <Tabs defaultValue="inbox">
          <TabsList className="rounded-none bg-surface">
            <TabsTrigger data-testid="admin-tab-inbox" value="inbox" className="rounded-none font-mono text-xs uppercase">Inbox {unread > 0 && `(${unread})`}</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-products" value="products" className="rounded-none font-mono text-xs uppercase">Prices</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders" className="rounded-none font-mono text-xs uppercase">Orders</TabsTrigger>
          </TabsList>
          <TabsContent value="inbox" className="mt-6"><Inbox messages={messages} reload={load} /></TabsContent>
          <TabsContent value="products" className="mt-6">
            <p className="mb-4 text-sm text-zinc-300">Products without a price show "Request Quote" on the site. Set a price to enable online checkout.</p>
            {products.map((p) => <PriceRow key={p.slug} p={p} reload={load} />)}
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            {!orders.length && <p data-testid="orders-empty" className="py-16 text-center font-mono text-sm text-zinc-400">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.session_id} data-testid={`order-${o.session_id}`} className="flex flex-wrap justify-between gap-3 border-b border-white/10 py-4">
                <div>
                  <p className="font-mono text-xs text-zinc-400">{new Date(o.created_at).toLocaleString()} {o.customer_email ? `/ ${o.customer_email}` : ""}</p>
                  <p className="text-sm">{(o.items || []).map((i) => `${i.slug} x${i.quantity}`).join(", ")}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-bold">{formatPrice(o.amount)}</p>
                  <p className={`font-mono text-xs uppercase ${o.payment_status === "paid" ? "text-volt" : "text-zinc-400"}`}>{o.payment_status}</p>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
